import type { MatchRecord } from '../hooks/useMatchRequests';

interface Props {
  rows: MatchRecord[];
  onRowClick: (row: MatchRecord) => void;
}

const roleLabel: Record<string, string> = {
  patient: '환자/보호자',
  caregiver: '간병인',
};

function formatDate(iso: string | null) {
  if (!iso) return '-';
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function formatAmount(amount: number | null) {
  if (amount == null) return '-';
  return `${amount.toLocaleString()}원`;
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-600', label: '대기중' },
    accepted: { bg: 'bg-green-50', text: 'text-green-600', label: '매칭완료' },
    rejected: { bg: 'bg-red-50', text: 'text-red-500', label: '거절됨' },
    completed: { bg: 'bg-blue-50', text: 'text-blue-600', label: '간병완료' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: '취소됨' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${s.bg} ${s.text}`}>
      {status === 'accepted' && <i className="ri-check-double-line text-[10px]" />}
      {s.label}
    </span>
  );
}

export default function MatchTable({ rows, onRowClick }: Props) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mb-4">
          <i className="ri-hand-heart-line text-2xl text-gray-300"></i>
        </div>
        <p className="text-sm font-semibold text-gray-400">매칭 내역이 없습니다</p>
        <p className="text-xs text-gray-300 mt-1">조건을 변경하거나 새로고침 해보세요</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            {['매칭번호', '요청자', '수신자', '상태', '간병기간', '장소', '금액', '연락처공개', '매칭일', ''].map((h) => (
              <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-3 py-3">
                <span className="font-mono text-xs font-semibold text-gray-700">
                  {row.match_number || row.id.slice(0, 8)}
                </span>
              </td>
              <td className="px-3 py-3">
                <p className="text-xs font-semibold text-gray-800">{row.requester_name}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">{roleLabel[row.requester_role] || row.requester_role}</span>
                  <span className="text-[10px] text-gray-300">{row.requester_phone}</span>
                </div>
              </td>
              <td className="px-3 py-3">
                <p className="text-xs font-semibold text-gray-800">{row.receiver_name}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">{roleLabel[row.receiver_role] || row.receiver_role}</span>
                  <span className="text-[10px] text-gray-300">{row.receiver_phone}</span>
                </div>
              </td>
              <td className="px-3 py-3">
                <StatusPill status={row.status} />
              </td>
              <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                {row.start_date ? `${formatDate(row.start_date)} ~ ${formatDate(row.end_date)}` : '-'}
              </td>
              <td className="px-3 py-3 text-xs text-gray-500 max-w-[120px] truncate">
                {row.location || '-'}
              </td>
              <td className="px-3 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap">
                {formatAmount(row.total_amount)}
              </td>
              <td className="px-3 py-3">
                {row.contact_revealed ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <i className="ri-check-line" />공개
                  </span>
                ) : (
                  <span className="text-[10px] text-gray-300">-</span>
                )}
              </td>
              <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
                {formatDate(row.created_at)}
              </td>
              <td className="px-3 py-3">
                <button
                  onClick={() => onRowClick(row)}
                  className="flex items-center gap-1 text-xs text-[#2E7D6B] hover:underline cursor-pointer whitespace-nowrap font-medium"
                >
                  상세 <i className="ri-arrow-right-s-line"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}